// hyparquet decodes the GeoParquet (WKB geometry -> GeoJSON via its geoparquet support) both libs
// load in a LAZY chunk so the table/map initial bundles don't grow.
import type {
  CategoryEntry,
  CovariateStackInfo,
  FieldCovariate,
  ManifestLayer,
} from "../types/contract";
import { geomCentroid } from "./cellCentroid";
import { api } from "../api";
import { mockCovariateValues, mockStackCells } from "./data/syntheticGrid";
import { isFittableBbox } from "./geo";
import { divergingColormapFor } from "./cogColor";
import {
  backendCategories,
  CLASS_PALETTE,
  numericLegendEntries,
  textLegendEntries,
} from "./classLegend";

// ---- pure helpers (unit-tested) -------------------------------------------------------

// A hyparquet file source: byte-addressable, sync (in-memory) OR async (HTTP Range). Both the whole-blob
// wrapper below and hyparquet's `asyncBufferFromUrl` (column-projected Range reads) satisfy it.
export type StackFile = {
  byteLength: number;
  slice: (start: number, end?: number) => ArrayBuffer | Promise<ArrayBuffer>;
};

// hyparquet AsyncBuffer over an in-memory ArrayBuffer (whole-blob fetch — the fallback when Range isn't
// available; the Range column-projected path — geometry once + only the viewed columns — is `buildStackFile`).
export function wrapBuffer(buf: ArrayBuffer): StackFile {
  return {
    byteLength: buf.byteLength,
    slice: (start: number, end?: number) => buf.slice(start, end),
  };
}

// ---- CRS handling ---------------------------------------------------------------------
// The stack cell grid is constructed in a metric CRS, so the blob's geometry
// may arrive in web-mercator, UTM, or an equal-area CRS — MapLibre needs lon/lat. The
// authoritative CRS is in the GeoParquet `geo` metadata (PROJJSON id); we map the EPSG
// code to a proj4 definition and inverse-project. Missing/unknown CRS falls back to a
// magnitude heuristic (|x| > 180 can only be meters -> assume web-mercator).
export function epsgProjString(code: number): string | null {
  if (code === 4326) return null; // already lon/lat
  if (code === 3857 || code === 900913)
    return "+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +no_defs";
  if (code >= 32601 && code <= 32660)
    return `+proj=utm +zone=${code - 32600} +datum=WGS84 +units=m +no_defs`; // WGS84 UTM north
  if (code >= 32701 && code <= 32760)
    return `+proj=utm +zone=${code - 32700} +south +datum=WGS84 +units=m +no_defs`;
  if (code >= 26901 && code <= 26923)
    return `+proj=utm +zone=${code - 26900} +datum=NAD83 +units=m +no_defs`; // NAD83 UTM
  if (code === 5070 || code === 6350 || code === 102003)
    return "+proj=aea +lat_0=23 +lon_0=-96 +lat_1=29.5 +lat_2=45.5 +x_0=0 +y_0=0 +datum=NAD83 +units=m +no_defs"; // CONUS Albers
  return "unknown"; // recognized as a code, but not one we can project
}

// EPSG code from the GeoParquet `geo` key-value metadata (PROJJSON `id`/`ids`); null when
// the metadata or crs is absent (GeoParquet default = OGC:CRS84, i.e. lon/lat).
export function crsCodeFromGeoMeta(geoJson: string | undefined): number | null {
  if (!geoJson) return null;
  try {
    const geo = JSON.parse(geoJson) as {
      primary_column?: string;
      columns?: Record<
        string,
        {
          crs?: {
            id?: { code?: number | string };
            ids?: { code?: number | string }[];
          } | null;
        }
      >;
    };
    const col = geo.columns?.[geo.primary_column ?? "geometry"];
    const raw = col?.crs?.id?.code ?? col?.crs?.ids?.[0]?.code;
    const code = raw == null ? null : Number(raw);
    return code != null && Number.isFinite(code) ? code : null;
  } catch {
    return null;
  }
}

const R = 6378137;
function mercToLonLat([x, y]: number[]): [number, number] {
  return [
    (x / R) * (180 / Math.PI),
    (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI),
  ];
}

type Ring = number[][];
type ToLonLat = (pos: number[]) => number[];
export function projectGeometry(
  geom: { type: string; coordinates: unknown },
  toLonLat: ToLonLat,
): { type: string; coordinates: unknown } {
  const project = (ring: Ring) => ring.map((pos) => toLonLat(pos));
  if (geom.type === "Polygon")
    return {
      type: "Polygon",
      coordinates: (geom.coordinates as Ring[]).map(project),
    };
  if (geom.type === "MultiPolygon")
    return {
      type: "MultiPolygon",
      coordinates: (geom.coordinates as Ring[][]).map((poly) =>
        poly.map(project),
      ),
    };
  return geom;
}

// Heuristic fallback (no usable CRS metadata): meters-scale x -> assume web-mercator.
export function normalizePolygon(geom: {
  type: string;
  coordinates: unknown;
}): { type: string; coordinates: unknown } {
  const rings =
    (geom.type === "Polygon"
      ? (geom.coordinates as Ring[])
      : (geom.coordinates as Ring[][]).flat()) ?? [];
  const needsReproject = rings.some((ring) =>
    ring.some(([x]) => Math.abs(x) > 180),
  );
  if (!needsReproject) return geom;
  return projectGeometry(geom, mercToLonLat);
}

// Same category->ramp vocabulary the mock/legend use (colormap names from §A7).
const COLORMAP_BY_CATEGORY: Record<string, string> = {
  ssurgo: "viridis",
  topography: "twi",
  satellite: "ndvi",
  climate: "climate",
  machine: "viridis",
  ec: "viridis",
  model: "viridis",
  supply: "viridis",
  productivity: "viridis",
  fertility: "viridis",
};

// `kind` categorical class-maps. `kind` numeric, and a missing kind, stay on the
// ramp. A versioned stack legend supplies the color and the label; anything else
// keeps the rank-colored legend so an unbackfilled stack paints as it does today.
export function categoriesForLayer(
  cov: FieldCovariate,
  values: (number | null)[],
): CategoryEntry[] | null {
  const present = values.filter(
    (v): v is number => v != null && Number.isFinite(v),
  );
  if (!present.length || cov.kind !== "categorical") return null;
  const backend = backendCategories(cov);
  if (backend) {
    const entries = numericLegendEntries(backend, present);
    if (entries) return entries;
  }
  const distinct = [...new Set(present)].sort((a, b) => a - b);
  const labels = new Map(
    (cov.categories ?? []).map((c) => [Number(c.value), c.label]),
  );
  return distinct.map((v, i) => ({
    class_value: v,
    color: CLASS_PALETTE[i % CLASS_PALETTE.length],
    label: labels.get(v) ?? String(v),
  }));
}

// One covariate column + the shared cell geometry -> a render-ready ManifestLayer (pure).
export function buildCovariateLayer(
  cov: FieldCovariate,
  info: Pick<CovariateStackInfo, "content_version" | "is_stale">,
  geoms: { type: string; coordinates: unknown }[],
  values: (number | null)[],
): ManifestLayer {
  const features: unknown[] = [];
  for (let i = 0; i < geoms.length; i++) {
    const v = values[i];
    if (v == null || !Number.isFinite(v)) continue; // missing cell value -> not drawn (transparent)
    features.push({
      type: "Feature",
      properties: { value: v },
      geometry: geoms[i],
    });
  }
  const present = values.filter(
    (v): v is number => v != null && Number.isFinite(v),
  );
  const cats = categoriesForLayer(cov, values);
  const base: ManifestLayer = {
    id: `cov-${cov.key}`,
    kind: "geojson",
    title: cov.title,
    inline: { type: "FeatureCollection", features },
    unit: cov.unit,
    direction: cov.direction ?? null, // authoritative red→green direction (CV1) → cogColor.lowerIsBetter
    default_visible: true,
    default_opacity: 0.85,
    order: 55, // above zones (50), below sample points (200)
    content_version: info.content_version,
    is_stale: info.is_stale,
  };
  if (cats) return { ...base, categories: cats };
  return {
    ...base,
    // DIVERGING for signed-deviation (diffusion variance) layers, derived from category+subcategory (DB mig 035
    // sends no colormap column); else the category default. `colormap` only marks the ramp KIND now (§A7).
    colormap:
      divergingColormapFor(cov) ??
      COLORMAP_BY_CATEGORY[cov.category] ??
      "viridis",
    // CV1 carries the field's real range; fall back to the decoded column's own extent.
    value_min: cov.value_min ?? (present.length ? Math.min(...present) : 0),
    value_max: cov.value_max ?? (present.length ? Math.max(...present) : 1),
  };
}

// The bare-soil TRUE-COLOR layer: a single clean composite rendered as each cell's actual sRGB
// color, from 3 columns (R/G/B, 0–255).
const clamp255 = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
export function buildSoilColorLayer(
  cov: FieldCovariate,
  info: Pick<CovariateStackInfo, "content_version" | "is_stale">,
  geoms: { type: string; coordinates: unknown }[],
  r: (number | null)[],
  g: (number | null)[],
  b: (number | null)[],
): ManifestLayer {
  const num = (v: number | null | undefined): v is number =>
    v != null && Number.isFinite(v);
  const features: unknown[] = [];
  for (let i = 0; i < geoms.length; i++) {
    if (!num(r[i]) || !num(g[i]) || !num(b[i])) continue;
    features.push({
      type: "Feature",
      properties: {
        color: `rgb(${clamp255(r[i]!)},${clamp255(g[i]!)},${clamp255(b[i]!)})`,
      },
      geometry: geoms[i],
    });
  }
  return {
    id: `cov-${cov.key}`,
    kind: "geojson",
    title: cov.title,
    inline: { type: "FeatureCollection", features },
    rgb: true,
    default_visible: true,
    default_opacity: 0.95, // true color reads best near-opaque
    order: 55,
    content_version: info.content_version,
    is_stale: info.is_stale,
  };
}

// A TEXT/categorical stack column (soil series, taxonomy, drainage class) -> a class map.
// The class is an integer index on each cell's `value`, so the numeric categorical fill
// draws it. The screen's own legend returns null above 40 classes. A versioned stack
// legend always draws; past 40 classes the legend groups the remainder.
export const MAX_STRING_CLASSES = 40;

function classLayer(
  cov: FieldCovariate,
  info: Pick<CovariateStackInfo, "content_version" | "is_stale">,
  features: unknown[],
  categories: CategoryEntry[],
): ManifestLayer {
  return {
    id: `cov-${cov.key}`,
    kind: "geojson",
    title: cov.title,
    inline: { type: "FeatureCollection", features },
    categories,
    default_visible: true,
    default_opacity: 0.85,
    order: 55,
    content_version: info.content_version,
    is_stale: info.is_stale,
  };
}

function layerFromTextLegend(
  cov: FieldCovariate,
  info: Pick<CovariateStackInfo, "content_version" | "is_stale">,
  geoms: { type: string; coordinates: unknown }[],
  raw: (string | number | null)[],
): ManifestLayer | null {
  const legend = textLegendEntries(backendCategories(cov) ?? [], raw);
  if (!legend) return null;
  const features: unknown[] = [];
  for (let i = 0; i < geoms.length; i++) {
    const v = raw[i];
    if (v == null) continue;
    const code = legend.codeOf.get(String(v));
    if (code == null) continue;
    features.push({
      type: "Feature",
      properties: { value: code },
      geometry: geoms[i],
    });
  }
  return features.length
    ? classLayer(cov, info, features, legend.entries)
    : null;
}

export function buildStringCategoricalLayer(
  cov: FieldCovariate,
  info: Pick<CovariateStackInfo, "content_version" | "is_stale">,
  geoms: { type: string; coordinates: unknown }[],
  raw: (string | number | null)[],
): ManifestLayer | null {
  if (backendCategories(cov)) {
    const versioned = layerFromTextLegend(cov, info, geoms, raw);
    if (versioned) return versioned;
  }
  const indexOf = new Map<string, number>();
  const labels: string[] = [];
  for (const v of raw) {
    if (v == null) continue;
    const s = String(v);
    if (!indexOf.has(s)) {
      indexOf.set(s, labels.length);
      labels.push(s);
    }
  }
  if (!labels.length || labels.length > MAX_STRING_CLASSES) return null; // empty, or too many to class-map
  const features: unknown[] = [];
  for (let i = 0; i < geoms.length; i++) {
    const v = raw[i];
    if (v == null) continue;
    const idx = indexOf.get(String(v));
    if (idx == null) continue;
    features.push({
      type: "Feature",
      properties: { value: idx },
      geometry: geoms[i],
    });
  }
  const categories: CategoryEntry[] = labels.map((label, i) => ({
    class_value: i,
    color: CLASS_PALETTE[i % CLASS_PALETTE.length],
    label,
  }));
  return classLayer(cov, info, features, categories);
}

// The 3 sRGB stack columns for a true-color covariate (explicit `rgb_keys`, else the `<key>_r/_g/_b` convention).
const rgbKeysOf = (cov: FieldCovariate): [string, string, string] => {
  const k = cov.rgb_keys;
  return k && k.length >= 3
    ? [k[0], k[1], k[2]]
    : [`${cov.key}_r`, `${cov.key}_g`, `${cov.key}_b`];
};

// ---- parquet decode (lazy-loaded libs) ------------------------------------------------

async function loadLibs() {
  const [{ parquetReadObjects, parquetMetadataAsync }, { compressors }, proj4] =
    await Promise.all([
      import("hyparquet"),
      import("hyparquet-compressors"),
      import("proj4").then((m) => m.default),
    ]);
  return { parquetReadObjects, parquetMetadataAsync, compressors, proj4 };
}

// One load for the tab. Column reads await this same promise.
let libsReady: Promise<Awaited<ReturnType<typeof loadLibs>>> | null = null;

function libs() {
  if (!libsReady) {
    libsReady = loadLibs().catch((err: unknown) => {
      libsReady = null;
      throw err;
    });
  }
  return libsReady;
}

export async function readGeoms(
  buf: ArrayBuffer,
): Promise<{ type: string; coordinates: unknown }[]> {
  return readGeomsFile(wrapBuffer(buf));
}
async function readGeomsFile(
  file: StackFile,
): Promise<{ type: string; coordinates: unknown }[]> {
  const { parquetReadObjects, parquetMetadataAsync, compressors, proj4 } =
    await libs();
  // Authoritative CRS from the GeoParquet metadata -> exact inverse projection.
  const meta = await parquetMetadataAsync(file);
  const geoKv = meta.key_value_metadata?.find((kv) => kv.key === "geo")?.value;
  const code = crsCodeFromGeoMeta(geoKv);
  const projStr = code == null ? null : epsgProjString(code);
  const rows = await parquetReadObjects({
    file,
    columns: ["geometry"],
    compressors,
    metadata: meta,
  });
  let out: { type: string; coordinates: unknown }[];
  if (projStr === null) {
    // lon/lat declared (or no CRS metadata at all) — heuristic still guards a mislabeled blob
    out = rows.map((r) =>
      normalizePolygon(r.geometry as { type: string; coordinates: unknown }),
    );
  } else if (projStr === "unknown") {
    console.warn(
      `[stack] unrecognized geometry CRS EPSG:${code} — falling back to magnitude heuristic`,
    );
    out = rows.map((r) =>
      normalizePolygon(r.geometry as { type: string; coordinates: unknown }),
    );
  } else {
    const conv = proj4(projStr);
    out = rows.map((r) =>
      projectGeometry(
        r.geometry as { type: string; coordinates: unknown },
        (pos) => conv.inverse([pos[0], pos[1]]),
      ),
    );
  }
  const first = ((out[0]?.coordinates as Ring[])?.[0] ?? [])[0];
  console.debug(
    `[stack] ${rows.length} cells decoded; CRS ${code ?? "lon/lat (default)"}; first vertex ${first ? first.map((n) => n.toFixed(5)).join(", ") : "n/a"}`,
  );
  return out;
}

export async function readColumn(
  buf: ArrayBuffer,
  key: string,
): Promise<(number | null)[]> {
  return readColumnFile(wrapBuffer(buf), key);
}
async function readColumnFile(
  file: StackFile,
  key: string,
): Promise<(number | null)[]> {
  const { parquetReadObjects, compressors } = await libs();
  const rows = await parquetReadObjects({ file, columns: [key], compressors });
  return rows.map((r) => toNum(r[key]));
}
// int64 columns arrive as BigInt; nulls stay null. Everything numeric -> number; non-numeric -> null.
const toNum = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Raw read that PRESERVES text — for TEXT/categorical columns (soil series, taxonomy, drainage class)
// whose values are class names, not numbers. `readColumn` coerces those to null (they aren't numeric)
// which reads as an empty column; this keeps the string so the caller can render a class map.
export async function readColumnRaw(
  buf: ArrayBuffer,
  key: string,
): Promise<(string | number | null)[]> {
  return readColumnRawFile(wrapBuffer(buf), key);
}
async function readColumnRawFile(
  file: StackFile,
  key: string,
): Promise<(string | number | null)[]> {
  const { parquetReadObjects, compressors } = await libs();
  const rows = await parquetReadObjects({ file, columns: [key], compressors });
  return rows.map((r) => {
    const v = r[key];
    if (v == null) return null;
    if (typeof v === "bigint") return Number(v);
    return typeof v === "number" ? v : String(v);
  });
}

// ---- per-field decoded-stack cache + orchestrator --------------------------------------

interface DecodedStack {
  contentVersion: string;
  file: StackFile; // hyparquet source — Range-fetcher (column-projected) OR the whole in-memory blob (fallback)
  geoms: { type: string; coordinates: unknown }[];
  columns: Map<string, (number | null)[]>;
}
const stackCache = new Map<string, DecodedStack>(); // fieldId -> decoded stack, LRU-bounded below

// Map tests paint a synthetic grid (there is no parquet blob). The time-series
// load tests turn this on so they count real column reads.
let readRealStackInTests = false;

export function setReadRealStackInTests(on: boolean): void {
  readRealStackInTests = on;
}

function syntheticStack(): boolean {
  return import.meta.env.MODE === "test" && !readRealStackInTests;
}

// One in-flight stack info fetch and one in-flight build per field + content
// version. Concurrent viewers share them instead of downloading the stack twice.
const infoInflight = new Map<string, Promise<CovariateStackInfo>>();
const buildInflight = new Map<string, Promise<DecodedStack>>();

export function resetDecodedStackForTests(): void {
  stackCache.clear();
  infoInflight.clear();
  buildInflight.clear();
}

function shareInflight<T>(
  map: Map<string, Promise<T>>,
  key: string,
  run: () => Promise<T>,
): Promise<T> {
  const pending = map.get(key);
  if (pending) return pending;
  const box: { current?: Promise<T> } = {};
  const promise = run().finally(() => {
    if (map.get(key) === box.current) map.delete(key);
  });
  box.current = promise;
  map.set(key, promise);
  return promise;
}

/*
 *
 * It was cached per field and never evicted, so closing the editor freed the store but not this:
 * each field visited grew the heap for the life of the tab. TWO entries, because every legitimate
 * sharer works on ONE field at a time (the preview panel, the editor and the animation all reuse
 * the ACTIVE field's stack — that sharing is the cache's purpose) and the second slot keeps a
 * just-left field warm for the back-and-forth pattern.
 */
const MAX_CACHED_STACKS = 2;

/* * Touch-on-use + evict-oldest-on-insert. Exported for the eviction test only. */
export function lruSet<K, V>(
  map: Map<K, V>,
  key: K,
  value: V,
  cap: number,
): void {
  if (map.has(key)) map.delete(key);
  map.set(key, value);
  while (map.size > cap) {
    const oldest = map.keys().next().value as K;
    map.delete(oldest);
  }
}

function lruGet<K, V>(map: Map<K, V>, key: K): V | undefined {
  const v = map.get(key);
  if (v !== undefined) {
    map.delete(key);
    map.set(key, v);
  }
  return v;
}

// Range column projection: fetch the footer + geometry once, then only the value column(s) rendered
// (~17x less than the whole 2.5 MB; BE-Light/DB confirmed the CV3 signed URL + bucket CORS honor
// cross-origin Range).
async function buildStackFile(signedUrl: string): Promise<StackFile> {
  try {
    const { asyncBufferFromUrl } = await import("hyparquet");
    const file = await asyncBufferFromUrl({ url: signedUrl });
    if (!file || !(file.byteLength > 0))
      throw new Error("range buffer unavailable");
    return file as StackFile;
  } catch {
    const res = await fetch(signedUrl); // whole-file fallback (zero regression vs. before)
    if (!res.ok) throw new Error(`stack download failed (${res.status})`);
    return wrapBuffer(await res.arrayBuffer());
  }
}

async function buildDecodedStack(
  fieldId: string,
  info: CovariateStackInfo,
): Promise<DecodedStack> {
  const file = await buildStackFile(info.signed_url);
  const stack: DecodedStack = {
    contentVersion: info.content_version,
    file,
    geoms: await readGeomsFile(file),
    columns: new Map(),
  };
  lruSet(stackCache, fieldId, stack, MAX_CACHED_STACKS);
  return stack;
}

async function decodedStackOf(
  fieldId: string,
): Promise<{ stack: DecodedStack; info: CovariateStackInfo }> {
  const info = await shareInflight(infoInflight, fieldId, () =>
    api.fetchCovariateStack(fieldId),
  );
  const cached = lruGet(stackCache, fieldId);
  if (cached && cached.contentVersion === info.content_version)
    return { stack: cached, info };
  const stack = await shareInflight(
    buildInflight,
    `${fieldId}\0${info.content_version}`,
    () => buildDecodedStack(fieldId, info),
  );
  return { stack, info };
}

async function columnOf(
  stack: DecodedStack,
  key: string,
): Promise<(number | null)[]> {
  let values = stack.columns.get(key);
  if (!values) {
    values = await readColumnFile(stack.file, key);
    stack.columns.set(key, values);
  }
  return values;
}

// "Pure Soil Type" (100% map unit) got ZERO features and the design failed, leaving the zones
// unchanged and Balanced ignored soil; it worked in dev only because the mock stores numeric class
// codes.
export function encodeClassIndices(
  raw: (string | number | null)[],
): (number | null)[] | null {
  const indexOf = new Map<string, number>();
  const out = raw.map((v) => {
    if (v == null) return null;
    const s = String(v);
    let idx = indexOf.get(s);
    if (idx == null) {
      idx = indexOf.size;
      indexOf.set(s, idx);
    }
    return idx;
  });
  return indexOf.size > 0 && indexOf.size <= MAX_STRING_CLASSES ? out : null;
}
async function columnForDesign(
  stack: DecodedStack,
  key: string,
): Promise<(number | null)[]> {
  const num = await columnOf(stack, key);
  if (num.some((v) => v != null && Number.isFinite(v))) return num;
  return encodeClassIndices(await readColumnRawFile(stack.file, key)) ?? num;
}

// Best-effort: any failure is swallowed (columnOf reads BATCHED: `parquetReadObjects` materializes
// ONE JS OBJECT PER CELL with a property per requested column. Past ~1,000 properties V8 stores
// objects in dictionary mode (~40–100 bytes/property), so a wide single read costs cells × columns
// × ~50+ bytes TRANSIENTLY — on a large field that's gigabytes, and it was crashing the tab ("Aw
// Snap" OOM) the moment relative Customize prefetched the whole catalog.
export const PREFETCH_BATCH = 24;
export function chunkKeys(keys: string[], size = PREFETCH_BATCH): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < keys.length; i += size) out.push(keys.slice(i, i + size));
  return out;
}
export async function prefetchColumns(
  fieldId: string,
  keys: string[],
): Promise<void> {
  try {
    const { stack } = await decodedStackOf(fieldId);
    const missing = keys.filter((k) => !stack.columns.has(k));
    if (!missing.length) return;
    const { parquetReadObjects, compressors } = await libs();
    for (const batch of chunkKeys(missing)) {
      const rows = await parquetReadObjects({
        file: stack.file,
        columns: batch,
        compressors,
      });
      for (const key of batch)
        stack.columns.set(
          key,
          rows.map((r) => toNum(r[key])),
        );
    }
  } catch {
    /* pre-fetch is an optimization only — on-demand columnOf still works */
  }
}

// Cell geometries + CENTER points + selected column values, aligned by index — the input
// for the sampling design (lib/samplingDesign: centroids are the candidate locations) and
// the zone design (lib/zoneDesign: the polygons dissolve into zones). Rides the same
// per-content_version cache.
/*
 * Identity of the CELL SET a design was built against — `contentVersion` busts on any boundary
 * change (and therefore on a merge), `n` catches a re-tile that kept the version. Callers that
 * persist per-CELL-INDEX state must record this and refuse to re-apply it across a change.
 */
export interface CellGridId {
  contentVersion: string;
  n: number;
}

export async function stackCellData(
  fieldId: string,
  keys: string[],
): Promise<{
  geoms: { type: string; coordinates: unknown }[];
  centroids: { lng: number; lat: number }[];
  features: (number | null)[][];
  grid: CellGridId;
}> {
  // Tests have no parquet blob. The synthetic grid keeps the map sidebar working.
  if (syntheticStack()) {
    const field = (await api.fetchFields()).find((f) => f.field_id === fieldId);
    if (!field || !isFittableBbox(field.bbox))
      throw new Error("mock field has no bbox for the synthetic stack");
    const m = mockStackCells(field.bbox);
    return { ...m, grid: { contentVersion: "mock", n: m.geoms.length } };
  }
  const { stack } = await decodedStackOf(fieldId);
  const centroids = stack.geoms.map(
    (g) => geomCentroid(g as GeoJSON.Geometry) ?? { lng: 0, lat: 0 },
  );
  const features: (number | null)[][] = [];
  for (const key of keys) {
    try {
      features.push(await columnForDesign(stack, key));
    } catch {
      features.push(centroids.map(() => null));
    }
  }
  return {
    geoms: stack.geoms,
    centroids,
    features,
    grid: { contentVersion: stack.contentVersion, n: stack.geoms.length },
  };
}

// Named stack columns aligned BY KEY (missing columns come back all-null so callers can
// zip options[j] ↔ values by key without index drift) + the cell centroids. Used by the
// fertility replacement's yield-history mode to read selected yield-year columns.
export async function stackColumns(
  fieldId: string,
  keys: string[],
): Promise<{
  centroids: { lng: number; lat: number }[];
  columns: Record<string, (number | null)[]>;
}> {
  const { centroids } = await stackCellData(fieldId, []);
  const { stack } = await decodedStackOf(fieldId);
  const columns: Record<string, (number | null)[]> = {};
  for (const key of keys) {
    try {
      columns[key] = await columnOf(stack, key);
    } catch {
      columns[key] = centroids.map(() => null);
    }
  }
  return { centroids, columns };
}

// Materialize one covariate as a map layer, fetching + decoding the field's stack blob on
// first use (cached per content_version — a boundary change rebuilds the stack and busts it).
export async function covariateLayerFromStack(
  fieldId: string,
  cov: FieldCovariate,
): Promise<ManifestLayer> {
  if (syntheticStack()) {
    const field = (await api.fetchFields()).find((f) => f.field_id === fieldId);
    if (!field || !isFittableBbox(field.bbox))
      throw new Error(`"${cov.title}" has no stack on this field.`);
    const cell = mockStackCells(field.bbox);
    if (cov.render === "rgb") {
      const base = mockCovariateValues(cell.centroids, 0, 1);
      const r = base.map((v) => (v == null ? null : 110 + v * 90));
      const g = base.map((v) => (v == null ? null : 75 + v * 70));
      const b = base.map((v) => (v == null ? null : 55 + v * 55));
      return buildSoilColorLayer(
        cov,
        { content_version: "mock", is_stale: false },
        cell.geoms,
        r,
        g,
        b,
      );
    }
    const values = mockCovariateValues(
      cell.centroids,
      cov.value_min ?? 0,
      cov.value_max ?? 1,
    );
    return buildCovariateLayer(
      cov,
      { content_version: "mock", is_stale: false },
      cell.geoms,
      values,
    );
  }
  const { stack, info } = await decodedStackOf(fieldId);
  if (cov.render === "rgb") {
    const [rk, gk, bk] = rgbKeysOf(cov);
    const [r, g, b] = await Promise.all([
      columnOf(stack, rk),
      columnOf(stack, gk),
      columnOf(stack, bk),
    ]);
    const layer = buildSoilColorLayer(cov, info, stack.geoms, r, g, b);
    // guard on the BUILT features (needs all 3 channels present on the SAME cell) — checking only R
    // would silently blank a layer where G/B is all-null or channel-nulls are misaligned
    if ((layer.inline as { features: unknown[] }).features.length === 0) {
      throw new Error(
        `"${cov.title}" has no bare-soil color values on this field's stack (needs R+G+B).`,
      );
    }
    return layer;
  }
  const values = await columnOf(stack, cov.key);
  if (!values.some((v) => v != null && Number.isFinite(v))) {
    // Unless CV1 says the column is `numeric` (then an empty numeric read is genuinely empty)
    // re-read raw and render a class map before declaring it empty; only a truly all-null column
    // throws.
    if (cov.kind !== "numeric") {
      const raw = await readColumnRawFile(stack.file, cov.key);
      const categorical = buildStringCategoricalLayer(
        cov,
        info,
        stack.geoms,
        raw,
      );
      if (categorical) return categorical;
    }
    throw new Error(
      `"${cov.title}" has no values on this field's stack (column is empty).`,
    );
  }
  return buildCovariateLayer(cov, info, stack.geoms, values);
}

/* * Lowest and highest finite cell on one covariate — the numbers drawn, not the color-scale legend. */
export async function covariateValueExtent(
  fieldId: string,
  cov: FieldCovariate,
): Promise<{ min: number; max: number } | null> {
  if (cov.render === "rgb") return null;
  let values: (number | null)[];
  if (syntheticStack()) {
    const field = (await api.fetchFields()).find((f) => f.field_id === fieldId);
    if (!field || !isFittableBbox(field.bbox)) return null;
    const cell = mockStackCells(field.bbox);
    values = mockCovariateValues(
      cell.centroids,
      cov.value_min ?? 0,
      cov.value_max ?? 1,
    );
  } else {
    try {
      const { stack } = await decodedStackOf(fieldId);
      values = await columnOf(stack, cov.key);
    } catch {
      return null;
    }
  }
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (v == null || !Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return min <= max ? { min, max } : null;
}
